<script setup lang="ts">
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import UserArea from "@/components/ui/UserArea.vue";
import {
  accessStatusLabels,
  providerLabels,
  statusClass,
  subscriptionStatusLabels,
} from "@/libs/adminUsers";
import {
  dateToUser,
  dateToUserWithTime,
  generateLinkWhatsapp,
} from "@/libs/filters";
import { gql } from "@apollo/client/core";
import { useQuery } from "@vue/apollo-composable";
import { computed, ref } from "vue";

type FilterState = {
  search: string;
  accessStatus: string;
  subscriptionStatus: string;
  provider: string;
  state: string;
  createdFrom: string;
  createdTo: string;
  accountType: string;
};

const emptyFilter = (): FilterState => ({
  search: "",
  accessStatus: "",
  subscriptionStatus: "",
  provider: "",
  state: "",
  createdFrom: "",
  createdTo: "",
  accountType: "USER",
});

const draftFilter = ref<FilterState>(emptyFilter());
const appliedFilter = ref<Record<string, string>>({ accountType: "USER" });
const currentPage = ref(0);
const pageCursors = ref<(string | null)[]>([null]);

const variables = computed(() => ({
  first: 50,
  after: pageCursors.value[currentPage.value],
  filter: appliedFilter.value,
}));

const { loading, error, result } = useQuery(
  gql`
    query AdminUsers(
      $first: Int!
      $after: String
      $filter: AdminUsersFilterInput
    ) {
      adminUsers(first: $first, after: $after, filter: $filter) {
        nodes {
          user {
            id
            name
            state
            createdAt
            phone
            asaasId
            email
            engagementPoints
            isAdmin
          }
          accessStatus
          subscriptionStatus
          provider
          validUntil
          nextDueDate
          localSubscriptionStatus
          subscriptionDescription
        }
        totalCount
        overview {
          total
          hasAccess
          noAccess
          deactivated
          courtesy
          noSubscription
          cancelled
          possiblyOverdue
          trial
          active
          expired
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  `,
  variables,
  { fetchPolicy: "no-cache" }
);

const connection = computed(() => result.value?.adminUsers);
const users = computed(() => connection.value?.nodes || []);
const overview = computed(() => connection.value?.overview);

const overviewCards = computed(() => [
  {
    label: "Usuários",
    value: overview.value?.total || 0,
    kind: "total",
    status: "",
  },
  {
    label: "Com acesso",
    value: overview.value?.hasAccess || 0,
    kind: "access",
    status: "HAS_ACCESS",
  },
  {
    label: "Sem acesso",
    value: overview.value?.noAccess || 0,
    kind: "access",
    status: "NO_ACCESS",
  },
  {
    label: "Em teste",
    value: overview.value?.trial || 0,
    kind: "subscription",
    status: "TRIAL",
  },
  {
    label: "Ativas",
    value: overview.value?.active || 0,
    kind: "subscription",
    status: "ACTIVE",
  },
  {
    label: subscriptionStatusLabels.POSSIBLY_OVERDUE,
    value: overview.value?.possiblyOverdue || 0,
    kind: "subscription",
    status: "POSSIBLY_OVERDUE",
  },
  {
    label: "Canceladas",
    value: overview.value?.cancelled || 0,
    kind: "subscription",
    status: "CANCELLED",
  },
  {
    label: "Expiradas",
    value: overview.value?.expired || 0,
    kind: "subscription",
    status: "EXPIRED",
  },
  {
    label: "Cortesia",
    value: overview.value?.courtesy || 0,
    kind: "subscription",
    status: "COURTESY",
  },
  {
    label: "Sem assinatura",
    value: overview.value?.noSubscription || 0,
    kind: "subscription",
    status: "NO_SUBSCRIPTION",
  },
]);

function toGraphqlFilter(filter: FilterState): Record<string, string> {
  const value: Record<string, string> = {};
  if (filter.search.trim()) value.search = filter.search.trim();
  if (filter.accessStatus) value.accessStatus = filter.accessStatus;
  if (filter.subscriptionStatus)
    value.subscriptionStatus = filter.subscriptionStatus;
  if (filter.provider) value.provider = filter.provider;
  if (filter.state) value.state = filter.state;
  if (filter.accountType) value.accountType = filter.accountType;
  if (filter.createdFrom)
    value.createdFrom = `${filter.createdFrom}T00:00:00.000Z`;
  if (filter.createdTo) value.createdTo = `${filter.createdTo}T23:59:59.999Z`;
  return value;
}

function resetPagination() {
  currentPage.value = 0;
  pageCursors.value = [null];
}

function applyFilters() {
  resetPagination();
  appliedFilter.value = toGraphqlFilter(draftFilter.value);
}

function clearFilters() {
  draftFilter.value = emptyFilter();
  applyFilters();
}

function applyCard(card: { kind: string; status: string }) {
  draftFilter.value.accessStatus = card.kind === "access" ? card.status : "";
  draftFilter.value.subscriptionStatus =
    card.kind === "subscription" ? card.status : "";
  applyFilters();
}

function nextPage() {
  const cursor = connection.value?.pageInfo.endCursor;
  if (!cursor || !connection.value?.pageInfo.hasNextPage) return;
  pageCursors.value[currentPage.value + 1] = cursor;
  currentPage.value += 1;
}

function previousPage() {
  if (currentPage.value > 0) currentPage.value -= 1;
}

function formatDate(date?: string): string {
  return date ? dateToUserWithTime(date) || "—" : "—";
}
</script>

<template>
  <DefaultLayout>
    <article class="container framework-slot">
      <UserArea title="Usuários">
        <div class="overview-grid" aria-label="Resumo dos usuários">
          <button
            v-for="card in overviewCards"
            :key="`${card.kind}-${card.status || 'total'}`"
            type="button"
            class="overview-card"
            :class="{
              selected:
                (card.kind === 'total' &&
                  !draftFilter.accessStatus &&
                  !draftFilter.subscriptionStatus) ||
                (card.kind === 'access' &&
                  draftFilter.accessStatus === card.status) ||
                (card.kind === 'subscription' &&
                  draftFilter.subscriptionStatus === card.status),
            }"
            @click="applyCard(card)"
          >
            <strong>{{ card.value }}</strong>
            <span>{{ card.label }}</span>
          </button>
        </div>

        <form class="filters" @submit.prevent="applyFilters">
          <div class="filter-search">
            <label for="admin-user-search">Buscar</label>
            <input
              id="admin-user-search"
              v-model="draftFilter.search"
              class="form-control"
              type="search"
              placeholder="Nome, e-mail ou telefone"
            />
          </div>
          <div>
            <label for="access-status">Acesso</label>
            <select
              id="access-status"
              v-model="draftFilter.accessStatus"
              class="form-select"
            >
              <option value="">Todos</option>
              <option
                v-for="(label, value) in accessStatusLabels"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
          </div>
          <div>
            <label for="subscription-status">Assinatura</label>
            <select
              id="subscription-status"
              v-model="draftFilter.subscriptionStatus"
              class="form-select"
            >
              <option value="">Todas</option>
              <option
                v-for="(label, value) in subscriptionStatusLabels"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
          </div>
          <div>
            <label for="provider">Provedor</label>
            <select
              id="provider"
              v-model="draftFilter.provider"
              class="form-select"
            >
              <option value="">Todos</option>
              <option
                v-for="(label, value) in providerLabels"
                :key="value"
                :value="value"
              >
                {{ label }}
              </option>
            </select>
          </div>
          <div>
            <label for="state">UF</label>
            <input
              id="state"
              v-model.trim="draftFilter.state"
              class="form-control"
              maxlength="2"
              placeholder="Ex.: CE"
            />
          </div>
          <div>
            <label for="account-type">Conta</label>
            <select
              id="account-type"
              v-model="draftFilter.accountType"
              class="form-select"
            >
              <option value="USER">Usuários</option>
              <option value="ADMIN">Administradores</option>
              <option value="ALL">Todos</option>
            </select>
          </div>
          <div>
            <label for="created-from">Cadastro inicial</label>
            <input
              id="created-from"
              v-model="draftFilter.createdFrom"
              class="form-control"
              type="date"
            />
          </div>
          <div>
            <label for="created-to">Cadastro final</label>
            <input
              id="created-to"
              v-model="draftFilter.createdTo"
              class="form-control"
              type="date"
            />
          </div>
          <div class="filter-actions">
            <button type="submit" class="btn btn-primary">
              Aplicar filtros
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              @click="clearFilters"
            >
              Limpar
            </button>
          </div>
        </form>

        <div v-if="loading" class="loading-state">Carregando usuários...</div>
        <div v-else-if="error" class="alert alert-danger">
          Não foi possível carregar os usuários.
        </div>
        <template v-else>
          <div class="results-header">
            <strong>{{ connection?.totalCount || 0 }}</strong>
            {{
              connection?.totalCount === 1
                ? "usuário encontrado"
                : "usuários encontrados"
            }}
          </div>
          <div v-if="!users.length" class="empty-state">
            Nenhum usuário corresponde aos filtros selecionados.
          </div>
          <div v-else class="users-content">
            <!-- Desktop Table (telas >= 992px) -->
            <div class="table-container d-none d-lg-block">
              <table class="table admin-users-table">
                <thead>
                  <tr>
                    <th class="col-user">Usuário</th>
                    <th class="col-contact">Contato</th>
                    <th class="col-provider">Provedor</th>
                    <th class="col-access">Acesso</th>
                    <th class="col-subscription">Assinatura</th>
                    <th class="col-validity">Validade / cobrança</th>
                    <th class="col-actions text-end">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in users" :key="item.user.id">
                    <td class="col-user">
                      <div class="user-name-wrapper">
                        <strong>{{
                          item.user.name || "Cadastro incompleto"
                        }}</strong>
                        <span v-if="item.user.isAdmin" class="badge-admin"
                          >Admin</span
                        >
                      </div>
                      <small
                        >{{ item.user.state || "UF não informada" }} ·
                        {{ formatDate(item.user.createdAt) }}</small
                      >
                    </td>
                    <td class="col-contact">
                      <a
                        :href="`mailto:${item.user.email}`"
                        class="contact-email"
                        >{{ item.user.email }}</a
                      >
                      <a
                        v-if="item.user.phone"
                        :href="generateLinkWhatsapp(item.user.phone)"
                        target="_blank"
                        class="contact-phone"
                        >{{ item.user.phone }}</a
                      >
                    </td>
                    <td class="col-provider">
                      {{ providerLabels[item.provider] }}
                    </td>
                    <td class="col-access">
                      <span
                        class="status-badge"
                        :class="statusClass(item.accessStatus)"
                        >{{ accessStatusLabels[item.accessStatus] }}</span
                      >
                    </td>
                    <td class="col-subscription">
                      <span
                        class="status-badge"
                        :class="statusClass(item.subscriptionStatus)"
                        >{{
                          subscriptionStatusLabels[item.subscriptionStatus]
                        }}</span
                      >
                      <small v-if="item.subscriptionDescription">{{
                        item.subscriptionDescription
                      }}</small>
                    </td>
                    <td class="col-validity">
                      <span>Validade: {{ dateToUser(item.validUntil) }}</span>
                      <small>Próxima: {{ dateToUser(item.nextDueDate) }}</small>
                    </td>
                    <td class="col-actions text-end">
                      <router-link
                        :to="`/adm/users/${item.user.id}`"
                        class="btn btn-secondary btn-sm"
                        >Detalhes</router-link
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile & Tablet Cards List (telas < 992px) -->
            <div class="user-cards-list d-lg-none">
              <article
                v-for="item in users"
                :key="`card-${item.user.id}`"
                class="user-card"
              >
                <div class="user-card-header">
                  <div class="user-card-title">
                    <strong class="user-card-name">{{
                      item.user.name || "Cadastro incompleto"
                    }}</strong>
                    <span v-if="item.user.isAdmin" class="badge-admin"
                      >Admin</span
                    >
                  </div>
                  <div class="user-card-meta">
                    {{ item.user.state || "UF não informada" }} ·
                    {{ formatDate(item.user.createdAt) }}
                  </div>
                </div>

                <div class="user-card-badges">
                  <span
                    class="status-badge"
                    :class="statusClass(item.accessStatus)"
                  >
                    {{ accessStatusLabels[item.accessStatus] }}
                  </span>
                  <span
                    class="status-badge"
                    :class="statusClass(item.subscriptionStatus)"
                  >
                    {{ subscriptionStatusLabels[item.subscriptionStatus] }}
                  </span>
                </div>

                <div class="user-card-body">
                  <div class="user-card-row">
                    <span class="card-row-label">Contato:</span>
                    <div class="card-row-value">
                      <a
                        :href="`mailto:${item.user.email}`"
                        class="contact-email"
                      >
                        {{ item.user.email }}
                      </a>
                      <a
                        v-if="item.user.phone"
                        :href="generateLinkWhatsapp(item.user.phone)"
                        target="_blank"
                        class="contact-phone"
                      >
                        {{ item.user.phone }}
                      </a>
                    </div>
                  </div>

                  <div
                    v-if="item.provider || item.subscriptionDescription"
                    class="user-card-row"
                  >
                    <span class="card-row-label">Assinatura:</span>
                    <div class="card-row-value">
                      <span>{{ providerLabels[item.provider] || "—" }}</span>
                      <span
                        v-if="item.subscriptionDescription"
                        class="card-plan-desc"
                      >
                        · {{ item.subscriptionDescription }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="item.validUntil || item.nextDueDate"
                    class="user-card-row"
                  >
                    <span class="card-row-label">Vigência:</span>
                    <div class="card-row-value">
                      <span v-if="item.validUntil">
                        Validade: {{ dateToUser(item.validUntil) }}
                      </span>
                      <span v-if="item.nextDueDate" class="card-next-due">
                        Próxima: {{ dateToUser(item.nextDueDate) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="user-card-footer">
                  <router-link
                    :to="`/adm/users/${item.user.id}`"
                    class="btn btn-secondary btn-sm user-card-btn"
                  >
                    Detalhes
                  </router-link>
                </div>
              </article>
            </div>
          </div>
          <nav class="pagination-controls" aria-label="Paginação dos usuários">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="currentPage === 0 || loading"
              @click="previousPage"
            >
              Anterior
            </button>
            <span>Página {{ currentPage + 1 }}</span>
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="!connection?.pageInfo.hasNextPage || loading"
              @click="nextPage"
            >
              Próxima
            </button>
          </nav>
        </template>
      </UserArea>
    </article>
  </DefaultLayout>
</template>

<style scoped>
.overview-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(110px, 1fr));
  gap: 0.65rem;
  margin-bottom: 1rem;
}
.overview-card {
  display: flex;
  min-height: 84px;
  flex-direction: column;
  justify-content: center;
  padding: 0.75rem;
  border: 1px solid #50514c;
  border-radius: 0.45rem;
  background: #22231f;
  color: var(--white-ice);
  text-align: left;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.overview-card:hover,
.overview-card.selected {
  border-color: var(--primary-color);
  transform: translateY(-1px);
}
.overview-card strong {
  color: var(--primary-color);
  font-size: 1.55rem;
  line-height: 1;
}
.overview-card span {
  margin-top: 0.45rem;
  color: #c7c7c1;
  font-size: 0.82rem;
}
.filters {
  display: grid;
  grid-template-columns: 2fr repeat(3, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--dark-grey);
  border-radius: 0.4rem;
  background: rgba(0, 0, 0, 0.12);
}
.filters label {
  display: block;
  margin-bottom: 0.25rem;
  color: #bdbdb8;
  font-size: 0.78rem;
}
.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  grid-column: span 2;
}
.results-header {
  margin: 0.3rem 0 0.75rem;
  color: #bdbdb8;
}
.results-header strong {
  color: var(--white-ice);
}
.table-container {
  overflow-x: auto;
  border: 1px solid var(--dark-grey);
  border-radius: 0.4rem;
}
.admin-users-table {
  width: 100%;
  margin-bottom: 0;
  color: var(--white-ice);
  table-layout: auto;
}
.admin-users-table thead {
  background: #20211e;
}
.admin-users-table th {
  border-color: #494a46;
  color: #aaa;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.6rem 0.5rem;
  white-space: nowrap;
}
.admin-users-table td {
  border-color: #494a46;
  vertical-align: middle;
  padding: 0.65rem 0.5rem;
  font-size: 0.82rem;
}
.admin-users-table tbody tr:hover {
  background: rgba(166, 199, 20, 0.04);
}
.admin-users-table td strong,
.admin-users-table td span,
.admin-users-table td small,
.admin-users-table td a {
  display: block;
}
.admin-users-table td small {
  margin-top: 0.2rem;
  color: #999;
}
.contact-email {
  word-break: break-all;
}
.contact-phone {
  margin-top: 0.2rem;
  color: var(--primary-color);
}
.user-name-wrapper {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}
.badge-admin {
  display: inline-block;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  background: rgba(166, 199, 20, 0.15);
  border: 1px solid rgba(166, 199, 20, 0.35);
  color: var(--primary-color);
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
}

/* Colunas desktop */
.col-user {
  min-width: 140px;
}
.col-contact {
  min-width: 150px;
}
.col-provider {
  width: 80px;
}
.col-access {
  width: 105px;
}
.col-subscription {
  width: 125px;
}
.col-validity {
  width: 150px;
}
.col-actions {
  width: 80px;
}

/* Cards Mobile & Tablet (<992px) */
.user-cards-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.user-card {
  border: 1px solid #494a46;
  border-radius: 0.5rem;
  background: #20211e;
  padding: 0.85rem;
  transition: border-color 0.15s ease;
}
.user-card:hover {
  border-color: #666;
}
.user-card-header {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.6rem;
}
.user-card-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}
.user-card-name {
  color: var(--white-ice);
  font-size: 1rem;
}
.user-card-meta {
  color: #999;
  font-size: 0.78rem;
}
.user-card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #333430;
}
.user-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.82rem;
}
.user-card-row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.card-row-label {
  color: #888;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.card-row-value {
  color: var(--white-ice);
}
.card-row-value a {
  display: block;
}
.card-plan-desc {
  color: #aaa;
}
.card-next-due {
  display: block;
  color: #999;
}
.user-card-footer {
  margin-top: 0.85rem;
  padding-top: 0.65rem;
  border-top: 1px solid #333430;
}
.user-card-btn {
  width: 100%;
}

.status-badge {
  width: fit-content;
  padding: 0.24rem 0.5rem;
  border-radius: 999px;
  background: #555;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.status-has-access,
.status-active,
.status-trial,
.status-courtesy {
  background: #53680b;
  color: #f1f8d4;
}
.status-no-access {
  background: #8f2929;
  color: #ffe6e6;
}
.status-possibly-overdue {
  background: #7b5a0a;
  color: #fff1c4;
}
.status-cancelled,
.status-expired,
.status-deactivated,
.status-no-subscription {
  background: #4d4d4d;
  color: #e5e5e0;
}
.loading-state,
.empty-state {
  padding: 2.5rem 1rem;
  border: 1px dashed #555;
  border-radius: 0.4rem;
  color: #aaa;
  text-align: center;
}
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 0;
}
.pagination-controls span {
  color: #aaa;
}
@media (max-width: 1199.98px) {
  .overview-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .filters {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 767.98px) {
  .overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .filters {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    grid-column: auto;
  }
  .filter-actions .btn {
    flex: 1;
  }
  .pagination-controls {
    justify-content: space-between;
  }
}
</style>
